/**
 * Moteur d'import CKA — dry-run + application idempotente (aucun compte de connexion).
 */
import {
  CANONICAL_DOJOS,
  CKA_HEALTH_VERSION,
  CKA_IMPORT_SOURCE,
  CKA_SOURCE_FILE,
  INSTRUCTOR_CATALOG,
  INSTRUCTOR_KEYS,
  KNOWN_EXISTING_PEOPLE,
  RICHARD_PIK,
  RICHARD_PIK_ID,
  RICHARD_PIK_MEMBERSHIP_ID,
  TO_COMPLETE,
  EMAIL_TO_VERIFY,
} from "./ckaImportConstants.js";
import {
  ageFromBirthDate,
  excelSerialToIsoDate,
  foldName,
  isNotApplicable,
  matchCanonicalDojo,
  matchInstructorKey,
  medicalHasCondition,
  normalizeBelt,
  normalizeEmail,
  normalizePhone,
  splitFullName,
} from "./ckaNormalize.js";

function emptyInstructorCounts() {
  return Object.fromEntries(
    Object.values(INSTRUCTOR_KEYS).map((key) => [
      key,
      { sourceRows: 0, uniqueMembers: 0, duplicates: 0, imported: 0 },
    ]),
  );
}

export function buildInstructorAuditTable(counts) {
  const labels = {
    [INSTRUCTOR_KEYS.GILLES]: "Gilles Lavigne",
    [INSTRUCTOR_KEYS.YANNICK]: "Yannick Kammer",
    [INSTRUCTOR_KEYS.STEPHANE]: "Stéphane Lachapelle",
    [INSTRUCTOR_KEYS.JAKI]: "Jaki St-Arnaud",
  };
  return Object.values(INSTRUCTOR_KEYS).map((key) => ({
    instructor: labels[key],
    key,
    sourceRows: counts[key].sourceRows,
    uniqueMembers: counts[key].uniqueMembers,
    duplicates: counts[key].duplicates,
    imported: counts[key].imported,
    zeroFoundNote: counts[key].sourceRows === 0 ? "0 trouvé — aucun membre inventé" : null,
  }));
}

function knownMatches(identity) {
  const email = identity.email;
  const folded = foldName(`${identity.firstName} ${identity.lastName}`);
  const phoneDigits = identity.phoneDigits;
  for (const known of KNOWN_EXISTING_PEOPLE) {
    if (email && known.email && email === known.email.toLowerCase()) return { known, via: "email" };
    const knownFold = foldName(`${known.firstName} ${known.lastName}`);
    const aliasFold = (known.aliases || []).map(foldName);
    const nameHit = folded === knownFold || aliasFold.includes(folded);
    if (nameHit && known.phone && phoneDigits && known.phone.replace(/\D/g, "") === phoneDigits) {
      return { known, via: "name+phone" };
    }
    if (nameHit && identity.birthDate && known.birthDate && identity.birthDate === known.birthDate) {
      return { known, via: "name+dob" };
    }
    if (nameHit && (known.instructorId || known.roles?.includes("instructor") || known.roles?.includes("hanshi"))) {
      if (folded === knownFold || aliasFold.includes(folded)) return { known, via: "name-known-official" };
    }
  }
  return null;
}

function parseParent(raw, studentEmail) {
  if (isNotApplicable(raw)) return null;
  const name = String(raw || "").replace(/\s+/g, " ").trim();
  if (!name) return null;
  const { firstName, lastName } = splitFullName(name);
  return {
    firstName: firstName || name,
    lastName: lastName || "",
    fullName: name,
    relation: TO_COMPLETE,
    phone: TO_COMPLETE,
    email: studentEmail || TO_COMPLETE,
  };
}

export function mapSourceRow(row) {
  const instructor = matchInstructorKey(row.chiefInstructor);
  if (!instructor.key) return null;
  const { firstName, lastName } = splitFullName(row.fullName);
  const emailInfo = normalizeEmail(row.email);
  const phoneInfo = normalizePhone(row.phone);
  const birthDate = excelSerialToIsoDate(row.birthDate) || (String(row.birthDate || "").match(/^\d{4}-\d{2}-\d{2}/) || [""])[0];
  const lastBeltDate = excelSerialToIsoDate(row.lastBeltDate) || (String(row.lastBeltDate || "").match(/^\d{4}-\d{2}-\d{2}/) || [""])[0];
  const age = ageFromBirthDate(birthDate) ?? (Number.isFinite(Number(row.age)) ? Math.round(Number(row.age)) : null);
  const parent = parseParent(row.parentGuardian, emailInfo.email);
  const minor = Boolean(parent) || (age != null && age < 16);
  const dojo = matchCanonicalDojo(row.dojo);
  const belt = normalizeBelt(row.lastBelt);
  const flags = [...instructor.flags, ...dojo.flags];
  if (emailInfo.suspect) flags.push("email-suspect");
  if (!firstName || !lastName) flags.push("name-incomplete");
  if (!birthDate) flags.push("birthdate-missing");
  if (belt.flag) flags.push(belt.flag);
  if (!dojo.key) flags.push("dojo-unmapped");
  if (age != null && age < 1) flags.push("age-implausible");

  const identity = {
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`.trim(),
    birthDate: birthDate || "",
    address: String(row.address || "").trim(),
    city: "",
    province: "",
    postalCode: "",
    phone: phoneInfo.display,
    phoneDigits: phoneInfo.digits,
    phoneRaw: phoneInfo.raw,
    email: emailInfo.email,
    gender: String(row.gender || "").trim(),
  };

  return {
    sourceRowId: String(row.id || "").trim(),
    instructorKey: instructor.key,
    instructorId: INSTRUCTOR_CATALOG[instructor.key].id,
    dojoKey: dojo.key,
    dojoId: dojo.key ? CANONICAL_DOJOS[dojo.key].id : null,
    kind: minor ? "minor" : "adult",
    identity,
    parent,
    belt,
    lastBeltDate: lastBeltDate || "",
    age,
    flags,
    health: {
      version: CKA_HEALTH_VERSION,
      completedAt: row.completionTime || null,
      completedBy: emailInfo.email || parent?.email || null,
      answers: {
        ckaMedicalSelections: String(row.medical || "").trim(),
        otherConditions: String(row.otherConditions || "").trim(),
        hasMedicalConditions: medicalHasCondition(row.medical, row.otherConditions) ? "yes" : "no",
        medicalConditionsDetails: [row.medical, row.otherConditions].filter((v) => String(v || "").trim()).join(" | "),
      },
    },
    sourceRaw: {
      fullName: row.fullName,
      chiefInstructor: row.chiefInstructor,
      dojo: row.dojo,
      lastBelt: row.lastBelt,
      email: row.email,
      phone: row.phone,
      birthDate: row.birthDate,
      parentGuardian: row.parentGuardian,
    },
  };
}

function memberDedupeKey(mapped) {
  if (mapped.identity.email && !mapped.flags.includes("email-suspect")) {
    return `email:${mapped.identity.email}|name:${foldName(mapped.identity.fullName)}`;
  }
  if (mapped.identity.phoneDigits && mapped.identity.phoneDigits.length >= 10) {
    return `phone:${mapped.identity.phoneDigits}|name:${foldName(mapped.identity.fullName)}`;
  }
  if (mapped.identity.birthDate) {
    return `dob:${mapped.identity.birthDate}|name:${foldName(mapped.identity.fullName)}`;
  }
  return `name:${foldName(mapped.identity.fullName)}|row:${mapped.sourceRowId}`;
}

function findExistingInSnapshot(snapshot, mapped) {
  const students = snapshot.etudiants || [];
  const instructors = snapshot.instructeurs || [];
  const memberships = snapshot.memberships || [];
  const email = mapped.identity.email;
  const folded = foldName(mapped.identity.fullName);
  const phone = mapped.identity.phoneDigits;

  const bySource = memberships.find((m) => m.source?.rowId === mapped.sourceRowId);
  if (bySource) return { type: "membership-source", record: bySource, via: "sourceRowId" };

  if (email) {
    const m = memberships.find((x) => String(x.identity?.email || "").toLowerCase() === email && foldName(`${x.identity?.firstName || ""} ${x.identity?.lastName || ""}`) === folded);
    if (m) return { type: "membership", record: m, via: "email+name" };
    const s = students.find((x) => String(x.courriel || "").toLowerCase() === email && foldName(`${x.prenom} ${x.nom}`) === folded);
    if (s) return { type: "student", record: s, via: "email+name" };
  }
  if (phone) {
    const s = students.find((x) => String(x.telephone || "").replace(/\D/g, "") === phone && foldName(`${x.prenom} ${x.nom}`) === folded);
    if (s) return { type: "student", record: s, via: "name+phone" };
  }
  const inst = instructors.find((x) => foldName(x.nom) === folded);
  if (inst) return { type: "instructor", record: inst, via: "name-instructor" };
  const known = knownMatches(mapped.identity);
  if (known) return { type: "known", record: known.known, via: known.via };
  return null;
}

function isDoubtfulOverwrite(existingValue, incomingValue) {
  const a = String(existingValue || "").trim();
  const b = String(incomingValue || "").trim();
  if (!b || b === TO_COMPLETE) return false;
  if (!a || a === TO_COMPLETE) return false;
  return foldName(a) !== foldName(b);
}

function enrichStudentPatch(existing, mapped) {
  const patch = {};
  const conflicts = [];
  const assign = (field, incoming, current) => {
    if (!incoming || incoming === TO_COMPLETE) return;
    if (!current || current === TO_COMPLETE) {
      patch[field] = incoming;
      return;
    }
    if (isDoubtfulOverwrite(current, incoming)) {
      conflicts.push({ field, existing: current, incoming });
      return;
    }
  };
  assign("prenom", mapped.identity.firstName, existing.prenom);
  assign("nom", mapped.identity.lastName, existing.nom);
  assign("courriel", mapped.identity.email, existing.courriel);
  assign("telephone", mapped.identity.phone, existing.telephone);
  assign("ceinture", mapped.belt.mapped ? mapped.belt.grade : mapped.belt.raw, existing.ceinture);
  if (!existing.dojo_id && mapped.dojoId) patch.dojo_id = mapped.dojoId;
  if (!existing.instructeur_id && mapped.instructorId) patch.instructeur_id = mapped.instructorId;
  return { patch, conflicts };
}

export function buildRichardPikRecord() {
  const dojo = CANONICAL_DOJOS["aamc-gatineau"];
  const instructor = INSTRUCTOR_CATALOG[INSTRUCTOR_KEYS.GILLES];
  return {
    student: {
      id: RICHARD_PIK_ID,
      prenom: RICHARD_PIK.firstName,
      nom: RICHARD_PIK.lastName,
      dojo_id: dojo.id,
      instructeur_id: instructor.id,
      groupe: TO_COMPLETE,
      ceinture: TO_COMPLETE,
      courriel: RICHARD_PIK.email,
      telephone: RICHARD_PIK.phone,
      date_naissance: "",
      adresse: TO_COMPLETE,
      kind: "adult",
      statut: "a_confirmer",
      demo: false,
      publicVisible: false,
      source: CKA_IMPORT_SOURCE,
      noLogin: true,
    },
    membership: {
      id: RICHARD_PIK_MEMBERSHIP_ID,
      kind: "adult",
      status: "form_incomplete",
      demo: false,
      origin: CKA_IMPORT_SOURCE,
      studentId: RICHARD_PIK_ID,
      noLogin: true,
      identity: {
        firstName: RICHARD_PIK.firstName,
        lastName: RICHARD_PIK.lastName,
        birthDate: "",
        address: TO_COMPLETE,
        city: TO_COMPLETE,
        province: TO_COMPLETE,
        postalCode: TO_COMPLETE,
        phone: RICHARD_PIK.phone,
        email: RICHARD_PIK.email,
        dojoId: dojo.id,
        instructorId: instructor.id,
      },
      parent: null,
      health: { version: CKA_HEALTH_VERSION, completedAt: null, completedBy: null, answers: {} },
      source: {
        name: CKA_IMPORT_SOURCE,
        file: "manual-richard-pik",
        rowId: "richard-pik",
        importedAt: null,
      },
    },
  };
}

function mappedToStudent(mapped) {
  const known = knownMatches(mapped.identity);
  const id = known?.known.studentId || `cka-etud-${mapped.sourceRowId}`;
  return {
    id,
    prenom: mapped.identity.firstName,
    nom: mapped.identity.lastName,
    dojo_id: mapped.dojoId,
    instructeur_id: mapped.instructorId,
    linkedInstructorId: known?.known.instructorId || null,
    personKey: known?.known.personKey || null,
    groupe: mapped.kind === "minor" ? "Mineur" : "Adulte",
    ceinture: mapped.belt.mapped ? mapped.belt.grade : mapped.belt.raw || TO_COMPLETE,
    ceinture_source: mapped.belt.raw,
    ceinture_date: mapped.lastBeltDate || "",
    courriel: mapped.identity.email,
    telephone: mapped.identity.phone,
    date_naissance: mapped.identity.birthDate || "",
    adresse: mapped.identity.address || "",
    kind: mapped.kind,
    statut: "actif",
    demo: false,
    publicVisible: false,
    source: CKA_IMPORT_SOURCE,
    sourceRowId: mapped.sourceRowId,
    noLogin: true,
    reviewFlags: mapped.flags,
  };
}

function mappedToMembership(mapped, studentId) {
  return {
    id: `cka-ms-${mapped.sourceRowId}`,
    kind: mapped.kind,
    status: "active",
    demo: false,
    origin: CKA_IMPORT_SOURCE,
    studentId,
    noLogin: true,
    adminPermissionsGranted: false,
    memberPermissionsGranted: false,
    identity: {
      firstName: mapped.identity.firstName,
      lastName: mapped.identity.lastName,
      birthDate: mapped.identity.birthDate || "",
      address: mapped.identity.address || "",
      city: mapped.identity.city || "",
      province: mapped.identity.province || "",
      postalCode: mapped.identity.postalCode || "",
      phone: mapped.identity.phone,
      email: mapped.identity.email,
      dojoId: mapped.dojoId,
      instructorId: mapped.instructorId,
    },
    parent: mapped.parent,
    health: mapped.health,
    source: {
      name: CKA_IMPORT_SOURCE,
      file: CKA_SOURCE_FILE,
      rowId: mapped.sourceRowId,
      original: mapped.sourceRaw,
    },
    reviewFlags: mapped.flags,
  };
}

/**
 * @param {object[]} sourceRows lignes CKA déjà parsées
 * @param {object} snapshot { etudiants, instructeurs, dojos, memberships }
 */
export function planCkaImport(sourceRows, snapshot = {}, { includeRichardPik = true, importedAt = "2026-09-02T12:00:00.000Z" } = {}) {
  const counts = emptyInstructorCounts();
  const mapped = [];
  const skippedUnmatched = [];
  for (const row of sourceRows) {
    const item = mapSourceRow(row);
    if (!item) {
      skippedUnmatched.push(row.chiefInstructor);
      continue;
    }
    counts[item.instructorKey].sourceRows += 1;
    mapped.push(item);
  }

  const seen = new Map();
  const unique = [];
  const duplicateRows = [];
  for (const item of mapped) {
    const key = memberDedupeKey(item);
    if (seen.has(key)) {
      duplicateRows.push({ keep: seen.get(key).sourceRowId, duplicate: item.sourceRowId, name: item.identity.fullName });
      counts[item.instructorKey].duplicates += 1;
      item.flags.push("duplicate-source-row");
      continue;
    }
    seen.set(key, item);
    unique.push(item);
    counts[item.instructorKey].uniqueMembers += 1;
  }

  const created = [];
  const enriched = [];
  const conflicts = [];
  const emailSuspects = unique.filter((m) => m.flags.includes("email-suspect"));
  const incomplete = unique.filter((m) => m.flags.includes("birthdate-missing") || m.flags.includes("name-incomplete"));

  const dojosToUpsert = [];
  const seenDojo = new Set();
  const instructorsToUpsert = [];
  const seenInst = new Set();

  for (const item of unique) {
    if (item.dojoId && !seenDojo.has(item.dojoId)) {
      seenDojo.add(item.dojoId);
      const canon = CANONICAL_DOJOS[item.dojoKey];
      dojosToUpsert.push({
        id: canon.id,
        nom: canon.nom,
        ville: canon.ville,
        adresse: TO_COMPLETE,
        telephone: "",
        courriel: "",
        sensei_id: INSTRUCTOR_CATALOG[canon.instructorKey].id,
        statut: "actif",
        demo: false,
        source: CKA_IMPORT_SOURCE,
      });
    }
    if (!seenInst.has(item.instructorId)) {
      seenInst.add(item.instructorId);
      const cat = INSTRUCTOR_CATALOG[item.instructorKey];
      instructorsToUpsert.push({
        id: cat.id,
        nom: cat.nom,
        titre: cat.titre,
        organisation: cat.organisation,
        dojo_id: item.dojoId,
        courriel: cat.courriel,
        telephone: "",
        statut: "actif",
        demo: false,
        source: CKA_IMPORT_SOURCE,
      });
    }

    const existing = findExistingInSnapshot(snapshot, item);
    const student = mappedToStudent(item);
    const membership = mappedToMembership(item, student.id);
    membership.source.importedAt = importedAt;

    if (existing) {
      const base = existing.type === "student" ? existing.record : snapshot.etudiants?.find((s) => s.id === existing.record.studentId) || {};
      const { patch, conflicts: rowConflicts } = enrichStudentPatch(base, item);
      enriched.push({ sourceRowId: item.sourceRowId, via: existing.via, personKey: existing.record.personKey || existing.record.id, patch });
      if (rowConflicts.length) {
        conflicts.push({ sourceRowId: item.sourceRowId, name: item.identity.fullName, via: existing.via, fields: rowConflicts });
      }
    } else {
      created.push(student.id);
    }
    counts[item.instructorKey].imported += 1;
    item._student = student;
    item._membership = membership;
    item._existing = existing;
  }

  let richard = null;
  if (includeRichardPik) {
    richard = buildRichardPikRecord();
    richard.membership.source.importedAt = importedAt;
    const already = (snapshot.etudiants || []).some((s) => s.id === RICHARD_PIK_ID)
      || (snapshot.memberships || []).some((m) => m.id === RICHARD_PIK_MEMBERSHIP_ID);
    richard.alreadyPresent = already;
  }

  return {
    totals: {
      sourceRowsConsidered: sourceRows.length,
      matchedRows: mapped.length,
      uniqueMembers: unique.length,
      duplicates: duplicateRows.length,
      created: created.length + (richard && !richard.alreadyPresent ? 1 : 0),
      enriched: enriched.length + (richard?.alreadyPresent ? 1 : 0),
      conflicts: conflicts.length,
      emailSuspects: emailSuspects.length,
      incomplete: incomplete.length,
      unmatchedInstructors: skippedUnmatched.length,
    },
    instructorTable: buildInstructorAuditTable(counts),
    instructorCounts: counts,
    duplicateRows,
    conflicts,
    emailSuspects: emailSuspects.map((m) => ({ sourceRowId: m.sourceRowId, email: m.identity.email, name: m.identity.fullName })),
    incomplete: incomplete.map((m) => ({ sourceRowId: m.sourceRowId, name: m.identity.fullName, flags: m.flags })),
    dojosToUpsert,
    instructorsToUpsert,
    uniqueMapped: unique,
    richard,
    dojoCorrespondence: Object.entries(CANONICAL_DOJOS).map(([key, d]) => ({
      key,
      canonical: d.nom,
      instructor: INSTRUCTOR_CATALOG[d.instructorKey].nom,
    })),
  };
}

export function applyCkaImportPlan(plan, snapshot) {
  const etudiants = [...(snapshot.etudiants || [])];
  const instructeurs = [...(snapshot.instructeurs || [])];
  const dojos = [...(snapshot.dojos || [])];
  const memberships = [...(snapshot.memberships || [])];

  const upsertById = (list, item) => {
    const idx = list.findIndex((r) => r.id === item.id);
    if (idx < 0) {
      list.push(item);
      return "created";
    }
    const current = list[idx];
    const next = { ...current };
    for (const [k, v] of Object.entries(item)) {
      if (v == null || v === "" || v === TO_COMPLETE) continue;
      if (k === "id") continue;
      if (!current[k] || current[k] === TO_COMPLETE || current[k] === "À confirmer") {
        next[k] = v;
        continue;
      }
      if (k === "titre" && item.id === "inst-gilles-lavigne") {
        next.titre = "Hanshi";
        next.organisation = "Shotokan";
        continue;
      }
      if (k === "nom" && item.id === "inst-jacki-st-arnaud") {
        next.nom = "Jaki St-Arnaud";
        continue;
      }
    }
    if (item.source) next.source = item.source;
    if (item.demo === false && current.demo) next.demo = current.demo;
    list[idx] = next;
    return "enriched";
  };

  for (const d of plan.dojosToUpsert) upsertById(dojos, d);
  for (const i of plan.instructorsToUpsert) {
    upsertById(instructeurs, i);
    if (i.id === "inst-gilles-lavigne") {
      const g = instructeurs.find((x) => x.id === i.id);
      if (g) {
        g.titre = "Hanshi";
        g.organisation = "Shotokan";
        g.nom = "Gilles Lavigne";
      }
    }
  }

  for (const item of plan.uniqueMapped) {
    upsertById(etudiants, item._student);
    upsertById(memberships, item._membership);
  }

  if (plan.richard) {
    upsertById(etudiants, plan.richard.student);
    upsertById(memberships, plan.richard.membership);
  }

  return { etudiants, instructeurs, dojos, memberships };
}

export function healthPayloadForExportGuard(membership) {
  return {
    id: membership.id,
    name: `${membership.identity?.firstName || ""} ${membership.identity?.lastName || ""}`.trim(),
    hasHealth: Boolean(membership.health),
  };
}
